/**
 * The `/v1` calls base config-as-code makes, in one place.
 *
 * Every path here is the Data surface's OWN path, verbatim — `DataClient` is the
 * single seam that knows where WayAI mounts it, so nothing in this layer spells
 * the `/api/data` proxy prefix (docs/reference/rekor-platform-contracts.md §1).
 */

import type { DataClient } from '../client.js';
import { pathSegment } from '../helpers.js';
import { omitUndefined } from './config-writer.js';
import {
  ENTITY_DIRS,
  type BaseConfig,
  type BaseMeta,
  type ConfigDiff,
  type EntityKind,
  type LintWarning,
} from './types.js';

export interface BaseRecord {
  id: string;
  environment?: string;
  integrations?: string;
  /** Data-plane analytics tier — create-only, so this is the tier the base keeps for life. */
  analytics?: string;
  origin_base_id?: string | null;
  org_id?: string;
  name?: string;
  description?: string;
  /** Advisories about the base that was just created (clone route only). */
  warnings?: { code?: string; message: string }[];
}

export interface ApplyResult {
  diff: ConfigDiff;
  warnings?: LintWarning[];
  new_trigger_secrets?: Record<string, string>;
  new_inbound_webhook_secrets?: Record<string, string>;
}

/**
 * Map a server record onto the on-disk `BaseMeta`, dropping fields the response
 * omitted rather than carrying `undefined`. The drop matters on push's
 * write-back, where the result is SPREAD over an existing meta: a field the
 * response left out must not wipe one the folder declares.
 */
export function metaFromRecord(record: BaseRecord, fallbackId: string): BaseMeta {
  return omitUndefined({
    base_id: record.id ?? fallbackId,
    origin_base_id: record.origin_base_id ?? undefined,
    name: record.name,
    description: record.description,
    environment: record.environment,
    integrations: record.integrations,
    analytics: record.analytics,
  }) as BaseMeta;
}

export function getBaseRecord(client: DataClient, id: string): Promise<BaseRecord> {
  return client.request<BaseRecord>('GET', `/v1/bases/${pathSegment(id, 'base id')}`);
}

export function getConfig(client: DataClient, id: string): Promise<BaseConfig> {
  return client.request<BaseConfig>('GET', `/v1/${pathSegment(id, 'base id')}/config`);
}

export function diffConfig(
  client: DataClient,
  id: string,
  local: BaseConfig,
): Promise<ConfigDiff> {
  return client.request<ConfigDiff>('POST', `/v1/${pathSegment(id, 'base id')}/config/diff`, local);
}

export function applyConfig(
  client: DataClient,
  id: string,
  local: BaseConfig,
): Promise<ApplyResult> {
  return client.request<ApplyResult>('PUT', `/v1/${pathSegment(id, 'base id')}/config`, local);
}

/**
 * Clone a preview from an origin base.
 *
 * `requestRaw`, not `request`: this route answers with a BARE body rather than
 * the `{ data }` envelope its siblings use, so an unwrapping read returns
 * `undefined` — and dereferencing `record.id` would throw AFTER the preview had
 * already been created server-side, leaving an orphan and no `base_id` in
 * `base.yaml`. The same body carries `warnings[]`, which push prints.
 */
export function createPreview(
  client: DataClient,
  originId: string,
  opts: { name: string; description?: string; analytics?: string },
): Promise<BaseRecord> {
  // `pathSegment`, not `basePathSegment`: the id at fault is the ORIGIN's, which is
  // immutable and not the caller's to re-spell, so the API's `assertDerivablePreviewId`
  // answers that case with the remedy rather than the charset. See `basePathSegment`.
  return client.requestRaw<BaseRecord>('POST', `/v1/${pathSegment(originId, 'origin base id')}/preview`, {
    name: opts.name,
    ...(opts.description ? { description: opts.description } : {}),
    // The tier is create-only, so this call is the ONLY chance to honor a tier
    // the folder declares. Omitted when absent so the clone default applies.
    ...(opts.analytics ? { analytics: opts.analytics } : {}),
  });
}

/** Delete one config entity via its per-entity route (used by `push --prune`). */
export function deleteEntity(
  client: DataClient,
  baseId: string,
  kind: EntityKind,
  id: string,
): Promise<unknown> {
  return client.request(
    'DELETE',
    `/v1/${pathSegment(baseId, 'base id')}/${ENTITY_DIRS[kind]}/${pathSegment(id, `${kind} id`)}`,
  );
}
